/* eslint-disable react/self-closing-comp */
import styleContent from 'shared-components/styles/content.module.css'
import styles from './Comments.module.css'
import clsx from 'clsx'
import { BasicStyledComponent } from 'shared-definitions/types'
import { PostContext } from 'shared-components/contexts/PostContext'
import dynamic from 'next/dynamic'
import ErrorBoundary from '../service/ErrorBoundary'
import { CommonDataContext } from '../contexts/CommonDataContext'
import { htmlReactParse } from 'shared-code/html'
import type { CommentHubConversationsFrame } from '../comments/CommentHubConversations'

const CommentHubConversations = dynamic(() => import('../comments/CommentHubConversations'), {
  ssr: false,
})

interface CommentsProps extends BasicStyledComponent {
  /** The host's own frame around the composer (`CommentHubConversationsFrame`). */
  frame?: CommentHubConversationsFrame
}

/**
 * The inline commentHub widget — the page's container drawn in place, under the comment policy.
 * Used by SoundGuys' community surfaces (`community/ThreadReplyBox`, `DiscussionAnswers`, through
 * `frame`). Article pages no longer mount it: they use `comments/conversation/ArticleConversation`
 * (the end-of-article block + the conversation panel). A build with no commentHub API URL renders
 * the section empty (see `useCommentHub`).
 */
const Comments: React.VFC<CommentsProps> = ({ className, frame }) => {
  const { engagementContainerId: id } = PostContext.useContainer()
  const { sponsored } = PostContext.useContainer()
  const {
    common: { strings },
  } = CommonDataContext.useContainer()

  if (sponsored || !id) {
    return null
  }

  return (
    <ErrorBoundary>
      <div data-container-type="content">
        <div className={clsx(styles.commentPolicy, styleContent.content)}>
          {htmlReactParse(strings.commentPolicy)}
        </div>
        <div
          data-container-type="content"
          className={clsx(styles.container, className)}
          id="comments-section"
        >
          <CommentHubConversations containerId={id} frame={frame} />
          {/* In the server HTML too: the widget's place, held until the host drops it. */}
          {frame?.standIn}
        </div>
      </div>
    </ErrorBoundary>
  )
}

export default Comments
